import React, { useEffect, useState } from 'react';
import { User, Mail, Phone, Lock, Eye, EyeOff, Shield, ShieldCheck, ArrowRight, Hash, Building2, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CommanServices } from '@/services/commanServices';
import InstitutionSwitcher from '@/components/InstitutionSwitcher';
import { canManageInstitutionAccess, isPlatformSuperadmin, readStoredUser } from '../auth/saasSession';
import { getWebAccessLevel, WEB_ACCESS } from '../auth/webAccess';
import './saasSecondaryPages.css';

const SAAS_LANGUAGE_KEY = 'disasterms_saas_language';
const SAAS_LANGUAGE_EVENT = 'disasterms:saas-language-changed';

const PROFILE_COPY = {
  en: {
    pageTitle: 'User Profile',
    pageSubtitle: 'Manage your platform account information and security.',
    loading: 'Loading profile...',
    active: 'Active',
    inactive: 'Inactive',
    save: 'Save changes',
    saving: 'Saving...',
    fullName: 'Full Name',
    email: 'Email',
    phone: 'Phone',
    impersonating: 'Profile editing is disabled while impersonating another account.',
    changePassword: 'Change Password',
    passwordSubtitle: 'Update your password to keep your account secure.',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    updatePassword: 'Update password',
    updating: 'Updating...',
    accountInformation: 'Account Information',
    role: 'Role',
    accessLevel: 'Access Level',
    employeeId: 'Employee ID',
    department: 'Department',
    accountStatus: 'Account Status',
    memberSince: 'Member Since',
    lastUpdated: 'Last Updated',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    fullNameRequired: 'Full name is required',
    emailRequired: 'Email is required',
    fillPasswordFields: 'Please fill in all password fields',
    passwordTooShort: 'New password must be at least 3 characters',
    passwordMismatch: 'New passwords do not match',
    profileUpdated: 'Profile updated successfully',
    profileLoadFailed: 'Failed to load profile',
    profileUpdateFailed: 'Failed to update profile',
    passwordChanged: 'Password changed successfully',
    passwordChangeFailed: 'Failed to change password',
  },
  ar: {
    pageTitle: 'الملف الشخصي',
    pageSubtitle: 'إدارة معلومات حساب المنصة وأمانه.',
    loading: 'جارٍ تحميل الملف الشخصي...',
    active: 'نشط',
    inactive: 'غير نشط',
    save: 'حفظ التغييرات',
    saving: 'جارٍ الحفظ...',
    fullName: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    phone: 'رقم الهاتف',
    impersonating: 'تم تعطيل تعديل الملف الشخصي أثناء انتحال حساب آخر.',
    changePassword: 'تغيير كلمة المرور',
    passwordSubtitle: 'حدّث كلمة المرور للحفاظ على أمان حسابك.',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'تأكيد كلمة المرور الجديدة',
    updatePassword: 'تحديث كلمة المرور',
    updating: 'جارٍ التحديث...',
    accountInformation: 'معلومات الحساب',
    role: 'الدور',
    accessLevel: 'مستوى الوصول',
    employeeId: 'رقم الموظف',
    department: 'القسم',
    accountStatus: 'حالة الحساب',
    memberSince: 'عضو منذ',
    lastUpdated: 'آخر تحديث',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    fullNameRequired: 'الاسم الكامل مطلوب',
    emailRequired: 'البريد الإلكتروني مطلوب',
    fillPasswordFields: 'يرجى تعبئة جميع حقول كلمة المرور',
    passwordTooShort: 'يجب ألا تقل كلمة المرور الجديدة عن 3 أحرف',
    passwordMismatch: 'كلمتا المرور الجديدتان غير متطابقتين',
    profileUpdated: 'تم تحديث الملف الشخصي بنجاح',
    profileLoadFailed: 'تعذر تحميل الملف الشخصي',
    profileUpdateFailed: 'تعذر تحديث الملف الشخصي',
    passwordChanged: 'تم تغيير كلمة المرور بنجاح',
    passwordChangeFailed: 'تعذر تغيير كلمة المرور',
  },
};

const mergeProfileIntoStoredSession = (updates) => {
  const stored = readStoredUser() || {};
  const next = {
    ...stored,
    ...(updates || {}),
  };
  if (stored.saas_context || updates?.saas_context) {
    next.saas_context = {
      ...(stored.saas_context || {}),
      ...(updates?.saas_context || {}),
    };
  }
  if (stored.platform_roles && !updates?.platform_roles) {
    next.platform_roles = stored.platform_roles;
  }
  return next;
};

const useSaasLanguage = () => {
  const readLanguage = () => localStorage.getItem(SAAS_LANGUAGE_KEY) === 'ar' ? 'ar' : 'en';
  const [language, setLanguage] = useState(readLanguage);

  useEffect(() => {
    const syncLanguage = () => setLanguage(readLanguage());
    window.addEventListener(SAAS_LANGUAGE_EVENT, syncLanguage);
    window.addEventListener('storage', syncLanguage);
    return () => {
      window.removeEventListener(SAAS_LANGUAGE_EVENT, syncLanguage);
      window.removeEventListener('storage', syncLanguage);
    };
  }, []);

  return language;
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const SaaSProfileView = ({
  loading,
  saving,
  changingPassword,
  profile,
  form,
  setForm,
  passwordForm,
  setPasswordForm,
  showCurrentPassword,
  setShowCurrentPassword,
  showNewPassword,
  setShowNewPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  profileError,
  passwordError,
  isImpersonating,
  handleProfileSubmit,
  handlePasswordSubmit,
  departmentNames,
  initials,
  language,
}) => {
  const copy = PROFILE_COPY[language];
  const displayedRole = 'Platform_user';
  const displayedAccessLevel = 'Platform';
  const accountRows = [
    { label: copy.role, value: displayedRole },
    { label: copy.accessLevel, value: displayedAccessLevel },
    { label: copy.employeeId, value: profile?.employee_id || '—', ltr: true },
    { label: copy.department, value: departmentNames.length > 0 ? departmentNames.join(', ') : '—' },
    {
      label: copy.accountStatus,
      value: profile?.is_active === false ? copy.inactive : copy.active,
      status: profile?.is_active === false ? 'inactive' : 'active',
    },
    { label: copy.memberSince, value: formatDate(profile?.created_at), ltr: true },
    { label: copy.lastUpdated, value: formatDate(profile?.updated_at), ltr: true },
  ];

  return (
    <main className="dms-saas-secondary-page" data-testid="saas-profile-page">
      <section className="dms-saas-secondary-heading">
        <div className="dms-saas-heading-icon"><User aria-hidden="true" /></div>
        <div>
          <h1>{copy.pageTitle}</h1>
          <p>{loading ? copy.loading : copy.pageSubtitle}</p>
        </div>
      </section>

      {loading ? (
        <section className="dms-saas-card dms-saas-empty-state">{copy.loading}</section>
      ) : (
        <div className="dms-saas-profile-layout">
          <form className="dms-saas-card dms-saas-profile-identity" onSubmit={handleProfileSubmit}>
            <div className="dms-saas-profile-banner">
              <div className="dms-saas-profile-person">
                <div className="dms-saas-profile-avatar" aria-hidden="true">{initials}</div>
                <div className="dms-saas-profile-name">
                  <div className="dms-saas-profile-title-line">
                    <h2>{profile?.full_name || '—'}</h2>
                    <span className={`dms-saas-status-pill ${profile?.is_active === false ? 'is-inactive' : 'is-active'}`}>
                      <CheckCircle2 aria-hidden="true" />
                      {profile?.is_active === false ? copy.inactive : copy.active}
                    </span>
                  </div>
                  <p dir="ltr">{profile?.email || '—'}</p>
                </div>
              </div>
              <button className="dms-saas-primary-button" type="submit" disabled={isImpersonating || saving}>
                {saving ? copy.saving : copy.save}
              </button>
            </div>

            {isImpersonating && (
              <div className="dms-saas-warning-message">{copy.impersonating}</div>
            )}

            <div className="dms-saas-profile-fields">
              <label>
                <span>{copy.fullName}</span>
                <span className="dms-saas-input-wrap">
                  <User aria-hidden="true" />
                  <input
                    value={form.full_name}
                    onChange={(event) => setForm((old) => ({ ...old, full_name: event.target.value }))}
                    disabled={isImpersonating || saving}
                    required
                  />
                </span>
              </label>
              <label>
                <span>{copy.email}</span>
                <span className="dms-saas-input-wrap">
                  <Mail aria-hidden="true" />
                  <input
                    type="email"
                    dir="ltr"
                    value={form.email}
                    onChange={(event) => setForm((old) => ({ ...old, email: event.target.value }))}
                    disabled={isImpersonating || saving}
                    required
                  />
                </span>
              </label>
              <label>
                <span>{copy.phone}</span>
                <span className="dms-saas-input-wrap">
                  <Phone aria-hidden="true" />
                  <input
                    type="tel"
                    dir="ltr"
                    value={form.phone_number}
                    onChange={(event) => setForm((old) => ({ ...old, phone_number: event.target.value }))}
                    disabled={isImpersonating || saving}
                  />
                </span>
              </label>
            </div>
            {profileError && <p className="dms-saas-error-message" role="alert">{profileError}</p>}
          </form>

          <div className="dms-saas-profile-bottom-grid">
            <section className="dms-saas-card dms-saas-password-card">
              <div className="dms-saas-card-heading">
                <div className="dms-saas-card-icon"><Lock aria-hidden="true" /></div>
                <div>
                  <h2>{copy.changePassword}</h2>
                  <p>{copy.passwordSubtitle}</p>
                </div>
              </div>
              <form onSubmit={handlePasswordSubmit} className="dms-saas-password-form">
                <label>
                  <span>{copy.currentPassword}</span>
                  <span className="dms-saas-input-wrap">
                    <Lock aria-hidden="true" />
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordForm.current_password}
                      onChange={(event) => setPasswordForm((old) => ({ ...old, current_password: event.target.value }))}
                      disabled={isImpersonating || changingPassword}
                      autoComplete="current-password"
                    />
                    <button type="button" onClick={() => setShowCurrentPassword((shown) => !shown)} aria-label={showCurrentPassword ? copy.hidePassword : copy.showPassword}>
                      {showCurrentPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                    </button>
                  </span>
                </label>
                <label>
                  <span>{copy.newPassword}</span>
                  <span className="dms-saas-input-wrap">
                    <Lock aria-hidden="true" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(event) => setPasswordForm((old) => ({ ...old, new_password: event.target.value }))}
                      disabled={isImpersonating || changingPassword}
                      autoComplete="new-password"
                    />
                    <button type="button" onClick={() => setShowNewPassword((shown) => !shown)} aria-label={showNewPassword ? copy.hidePassword : copy.showPassword}>
                      {showNewPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                    </button>
                  </span>
                </label>
                <label>
                  <span>{copy.confirmPassword}</span>
                  <span className="dms-saas-input-wrap">
                    <Lock aria-hidden="true" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(event) => setPasswordForm((old) => ({ ...old, confirm_password: event.target.value }))}
                      disabled={isImpersonating || changingPassword}
                      autoComplete="new-password"
                    />
                    <button type="button" onClick={() => setShowConfirmPassword((shown) => !shown)} aria-label={showConfirmPassword ? copy.hidePassword : copy.showPassword}>
                      {showConfirmPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                    </button>
                  </span>
                </label>
                {passwordError && <p className="dms-saas-error-message" role="alert">{passwordError}</p>}
                <button className="dms-saas-secondary-button" type="submit" disabled={isImpersonating || changingPassword}>
                  {changingPassword ? copy.updating : copy.updatePassword}
                </button>
              </form>
            </section>

            <section className="dms-saas-card dms-saas-account-card">
              <div className="dms-saas-card-heading">
                <div className="dms-saas-card-icon"><Shield aria-hidden="true" /></div>
                <h2>{copy.accountInformation}</h2>
              </div>
              <dl className="dms-saas-account-list">
                {accountRows.map((row) => (
                  <div key={row.label}>
                    <dt>{row.label}</dt>
                    <dd dir={row.ltr ? 'ltr' : undefined} className={row.status ? `status-${row.status}` : ''}>
                      {row.status === 'active' && <span className="dms-saas-account-status-dot" />}
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          </div>
        </div>
      )}
    </main>
  );
};

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', phone_number: '' });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const language = useSaasLanguage();
  const copy = PROFILE_COPY[language];
  const storedPlatformAccount = isPlatformSuperadmin(readStoredUser());
  const actionCopy = storedPlatformAccount ? copy : PROFILE_COPY.en;

  const isImpersonating = !!localStorage.getItem('impersonating');

  const loadProfile = async () => {
    setLoading(true);
    setProfileError('');
    try {
      const [meRes, deptRes] = await Promise.all([
        CommanServices.getMe(),
        storedPlatformAccount
          ? Promise.resolve(null)
          : CommanServices.getLoginDepartment().catch(() => null),
      ]);

      if (deptRes?.status) {
        setDepartmentOptions(deptRes.data || []);
      }

      if (meRes?.status && meRes.data) {
        const nextProfile = mergeProfileIntoStoredSession(meRes.data);
        setProfile(nextProfile);
        setForm({
          full_name: nextProfile.full_name || '',
          email: nextProfile.email || '',
          phone_number: nextProfile.phone_number || '',
        });
        localStorage.setItem('user', JSON.stringify(nextProfile));
      } else {
        const cached = localStorage.getItem('user');
        if (cached) {
          const user = JSON.parse(cached);
          setProfile(user);
          setForm({
            full_name: user.full_name || '',
            email: user.email || '',
            phone_number: user.phone_number || '',
          });
        }
        setProfileError(meRes?.message || actionCopy.profileLoadFailed);
      }
    } catch (err) {
      setProfileError(err?.message || actionCopy.profileLoadFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const departmentNames = (profile?.departments_units?.length
    ? profile.departments_units
    : profile?.department_unit
      ? [profile.department_unit]
      : []
  )
    .map((id) => departmentOptions.find((d) => String(d.id) === String(id))?.department_name)
    .filter(Boolean);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');

    if (!form.full_name.trim()) {
      setProfileError(actionCopy.fullNameRequired);
      return;
    }
    if (!form.email.trim()) {
      setProfileError(actionCopy.emailRequired);
      return;
    }

    setSaving(true);
    try {
      const res = await CommanServices.updateProfile({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone_number: form.phone_number.trim(),
      });

      if (res?.status && res.data) {
        const nextProfile = mergeProfileIntoStoredSession(res.data);
        setProfile(nextProfile);
        localStorage.setItem('user', JSON.stringify(nextProfile));
        window.dispatchEvent(new Event('userProfileUpdated'));
        toast.success(actionCopy.profileUpdated);
      } else {
        const msg = res?.message || actionCopy.profileUpdateFailed;
        setProfileError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = err?.message || actionCopy.profileUpdateFailed;
      setProfileError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      setPasswordError(actionCopy.fillPasswordFields);
      return;
    }
    if (passwordForm.new_password.length < 3) {
      setPasswordError(actionCopy.passwordTooShort);
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError(actionCopy.passwordMismatch);
      return;
    }

    setChangingPassword(true);
    try {
      const res = await CommanServices.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });

      if (res?.status) {
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        toast.success(actionCopy.passwordChanged);
      } else {
        const msg = res?.message || actionCopy.passwordChangeFailed;
        setPasswordError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = err?.message || actionCopy.passwordChangeFailed;
      setPasswordError(msg);
      toast.error(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  const initials = profile?.full_name?.charAt(0)?.toUpperCase() || '?';
  const isSaasAccount = isPlatformSuperadmin(profile) || isPlatformSuperadmin(readStoredUser());
  const profileAccessLevel = getWebAccessLevel(profile || readStoredUser());
  const canOpenPoliciesAndSupport = !isSaasAccount
    && (
      canManageInstitutionAccess(profile || readStoredUser())
      || profileAccessLevel === WEB_ACCESS.ADMIN
      || profileAccessLevel === WEB_ACCESS.SECURITY_ADMIN
    );

  if (isSaasAccount) {
    return (
      <SaaSProfileView
        loading={loading}
        saving={saving}
        changingPassword={changingPassword}
        profile={profile}
        form={form}
        setForm={setForm}
        passwordForm={passwordForm}
        setPasswordForm={setPasswordForm}
        showCurrentPassword={showCurrentPassword}
        setShowCurrentPassword={setShowCurrentPassword}
        showNewPassword={showNewPassword}
        setShowNewPassword={setShowNewPassword}
        showConfirmPassword={showConfirmPassword}
        setShowConfirmPassword={setShowConfirmPassword}
        profileError={profileError}
        passwordError={passwordError}
        isImpersonating={isImpersonating}
        handleProfileSubmit={handleProfileSubmit}
        handlePasswordSubmit={handlePasswordSubmit}
        departmentNames={departmentNames}
        initials={initials}
        language={language}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 w-full h-full bg-gray-50 overflow-y-auto" data-testid="profile-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
          <User className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-gray-900">User Profile</h1>
          <p className="text-sm text-gray-500">
            {loading ? 'Loading profile...' : 'Manage your account information and security'}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-sm text-gray-500">
          Loading profile...
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center ring-2 ring-blue-100">
                  <span className="text-white text-2xl font-bold">{initials}</span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{profile?.full_name || '—'}</h2>
                  <p className="text-sm text-gray-500">{profile?.email || '—'}</p>
                </div>
              </div>

              {isImpersonating && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Profile editing is disabled while impersonating another account.
                </div>
              )}

              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="full_name"
                      value={form.full_name}
                      onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                      className="pl-10"
                      disabled={isImpersonating || saving}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className="pl-10"
                      disabled={isImpersonating || saving}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone_number">Phone Number</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="phone_number"
                      value={form.phone_number}
                      onChange={(e) => setForm((f) => ({ ...f, phone_number: e.target.value }))}
                      className="pl-10"
                      disabled={isImpersonating || saving}
                    />
                  </div>
                </div>

                {profileError && <p className="text-sm text-red-600">{profileError}</p>}

                <Button type="submit" disabled={isImpersonating || saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Change Password</h3>
              <p className="text-sm text-gray-500 mb-4">Update your password to keep your account secure.</p>

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="current_password">Current Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="current_password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm((f) => ({ ...f, current_password: e.target.value }))}
                      className="pl-10 pr-10"
                      disabled={isImpersonating || changingPassword}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      onClick={() => setShowCurrentPassword((s) => !s)}
                      tabIndex={-1}
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="new_password">New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="new_password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm((f) => ({ ...f, new_password: e.target.value }))}
                      className="pl-10 pr-10"
                      disabled={isImpersonating || changingPassword}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      onClick={() => setShowNewPassword((s) => !s)}
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm((f) => ({ ...f, confirm_password: e.target.value }))}
                      className="pl-10 pr-10"
                      disabled={isImpersonating || changingPassword}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      onClick={() => setShowConfirmPassword((s) => !s)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {passwordError && <p className="text-sm text-red-600">{passwordError}</p>}

                <Button type="submit" variant="outline" disabled={isImpersonating || changingPassword}>
                  {changingPassword ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
              <dl className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <Shield className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <dt className="text-gray-500">Role</dt>
                    <dd className="font-medium text-gray-900 capitalize">{profile?.role || 'user'}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <dt className="text-gray-500">Access Level</dt>
                    <dd className="font-medium text-gray-900">{profile?.access_level || 'Reporter'}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Hash className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <dt className="text-gray-500">Employee ID</dt>
                    <dd className="font-medium text-gray-900">{profile?.employee_id || '—'}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <dt className="text-gray-500">Department / Unit</dt>
                    <dd className="font-medium text-gray-900">
                      {departmentNames.length > 0 ? departmentNames.join(', ') : '—'}
                    </dd>
                  </div>
                </div>
                <div>
                  <dt className="text-gray-500">Account Status</dt>
                  <dd className="font-medium text-gray-900">
                    {profile?.is_active === false ? 'Inactive' : 'Active'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Member Since</dt>
                  <dd className="font-medium text-gray-900">{formatDate(profile?.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Last Updated</dt>
                  <dd className="font-medium text-gray-900">{formatDate(profile?.updated_at)}</dd>
                </div>
              </dl>
            </div>
            {canOpenPoliciesAndSupport && (
              <Link
                to="/access-administration"
                className="group flex items-center gap-4 rounded-xl border border-blue-100 bg-white p-5 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                aria-label="Open Policies & Support"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-100">
                  <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-gray-900">Policies &amp; Support</span>
                  <span className="mt-1 block text-sm leading-5 text-gray-500">
                    Manage institution policies, post-close access and support requests.
                  </span>
                </span>
                <ArrowRight className="h-5 w-5 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-blue-600" aria-hidden="true" />
              </Link>
            )}
            <InstitutionSwitcher variant="profile" institutionAccountsOnly />
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
